import { WarningAmber } from '@mui/icons-material';
import { styled, Typography } from '@mui/material';
import { L } from 'harmony-language';
import React from 'react';
import { Load } from '../../../../types';
import { getConvertedDistance } from '../../../../utils/distance-utils';
import { AgisticsTooltip } from '../../../shared/agistics-tooltip';
import { useLadenUnladen } from '../../../shared/hooks/use-laden-unladen';
import { useWeights } from '../../../shared/hooks/use-weights';
import { TravelTimesCache } from '../../../travel-times/use-travel-times';
import { RunningTime } from '../../table-components/running-time';
import { validateStopQuantitiesAndWeights } from '../utils/validate-drafts';

interface BulkScheduleRouteFiguresProps {
    drafts: Load[];
    travelTimesCache: TravelTimesCache | undefined;
    driverHomeLocationId?: number;
}

export const BulkScheduleRouteFigures = (props: BulkScheduleRouteFiguresProps) => {
    const { drafts, travelTimesCache, driverHomeLocationId } = props;
    const { convertFromGrams, weightPrecision } = useWeights();

    const scheduledStopsInOrder = drafts.flatMap(draft => draft.stops);
    const errors = validateStopQuantitiesAndWeights(scheduledStopsInOrder, convertFromGrams, weightPrecision);
    
    const { ladenMeters, unladenMeters } = useLadenUnladen(scheduledStopsInOrder, travelTimesCache, driverHomeLocationId);
    const ladenConverted = getConvertedDistance(ladenMeters);
    const unladenConverted = getConvertedDistance(unladenMeters);
    const ladenPercentage = (ladenMeters && (ladenMeters / (ladenMeters + unladenMeters)) * 100).toFixed() || 0;

    return (
        <div style={{ margin: '0.5rem 0' }}>
            <FlexRow>
                <CalculationTitle>{`${L.estimatedScheduleTime()}:`}</CalculationTitle>
                <Typography><RunningTime selectedLoads={drafts} driverHomeLocationId={driverHomeLocationId} /></Typography>
            </FlexRow>        
            {errors.length ? (
                <FlexRow>
                    <CalculationTitle>{L.ladenUnladenUnavailable()}</CalculationTitle>
                    <AgisticsTooltip tooltip={errors.map((error: string, i: number) => <Typography key={`${i}-${error}`}>{error}</Typography>)}>
                        <WarningAmber color='error'/>
                    </AgisticsTooltip>
                </FlexRow>
            ) : (
                <>
                    <FlexRow>
                        <CalculationTitle>{`${L.ladenDistance()}:`}</CalculationTitle>
                        <Typography>{`${ladenConverted.convertedDistance || 0} ${ladenConverted.abbreviation}`}</Typography>
                    </FlexRow>
                    <FlexRow>
                        <CalculationTitle>{`${L.unladenDistance()}:`}</CalculationTitle>
                        <Typography>{`${unladenConverted.convertedDistance || 0} ${unladenConverted.abbreviation}`}</Typography>
                    </FlexRow>
                    <FlexRow>
                        <CalculationTitle>{`${L.ladenPercentage()}:`}</CalculationTitle>
                        <Typography>{`${ladenPercentage}%`}</Typography>
                    </FlexRow>
                </>
            )}
        </div>
    )
}

const FlexRow = styled('div')({
    display: 'flex',
});

const CalculationTitle = styled(Typography)({
    marginRight: '0.5rem',
    fontWeight: 'bold',
});